import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconKey, IconMapPin, IconChevronDown, IconChevronUp, IconClock, IconHome } from '@tabler/icons-react'
import { cn } from '@/lib/utils'

interface DoorCodeSectionProps {
  /** The door access code */
  doorCode: string
  /** Assigned room number (e.g., "301") */
  assignedRoom?: string
  /** Check-in date for display */
  checkInDate?: string
  /** Check-out date for display */
  checkOutDate?: string
  /** Property name */
  propertyName?: string
  /** Whether the section starts collapsed */
  defaultCollapsed?: boolean
  /** Custom access instructions (markdown/text) */
  accessInstructions?: string
  /** Location/address info */
  locationInfo?: {
    address?: string
    city?: string
    coordinates?: { lat: number; lng: number }
    googleMapsUrl?: string
  }
}

/**
 * DoorCodeSection - Displays the door access code and entry instructions
 * 
 * Shown in the GuestPortal after successful registration when:
 * - now() >= doorCodeVisibleFrom
 * - now() <= doorCodeVisibleUntil
 * 
 * Features:
 * - Collapsible by default (configurable)
 * - Shows door code prominently
 * - Displays assigned room
 * - Access instructions
 * - Location with Google Maps link
 */
export function DoorCodeSection({
  doorCode,
  assignedRoom,
  checkInDate,
  checkOutDate,
  propertyName = 'Hotel Guilleumes',
  defaultCollapsed = true,
  accessInstructions,
  locationInfo,
}: DoorCodeSectionProps) {
  const { t } = useTranslation('guestPortal')
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed)

  // Default access instructions if none provided
  const defaultInstructions = t('doorCode.defaultInstructions', {
    defaultValue: `1. Dirigiu-vos a l'entrada principal
2. Introduïu el codi al teclat numèric
3. Espereu el senyal verd i empenyeu la porta
4. La vostra habitació és a la planta indicada`
  })

  const instructions = accessInstructions || defaultInstructions

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-2 border-emerald-200 rounded-3xl shadow-xl overflow-hidden">
      {/* Header - Always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-emerald-50/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-3 rounded-2xl shadow-lg">
            <IconKey className="h-7 w-7 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold text-gray-900">
              {t('doorCode.title', 'Codi d\'accés')}
            </h3>
            <p className="text-sm text-emerald-600 font-medium">
              {t('doorCode.subtitle', 'Informació per entrar a l\'allotjament')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Preview of code when collapsed */}
          {!isExpanded && (
            <div className="bg-emerald-100 px-4 py-2 rounded-xl">
              <span className="font-mono text-2xl font-bold text-emerald-700 tracking-wider">
                {doorCode}
              </span>
            </div>
          )}
          <div className={cn(
            "p-2 rounded-full transition-colors",
            isExpanded ? "bg-emerald-100" : "bg-gray-100"
          )}>
            {isExpanded ? (
              <IconChevronUp className="h-5 w-5 text-emerald-600" />
            ) : (
              <IconChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>
      </button>

      {/* Expandable content */}
      <div className={cn(
        "transition-all duration-300 ease-in-out overflow-hidden",
        isExpanded ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className="px-6 pb-6 space-y-6">
          {/* Door Code - Prominent display */}
          <div className="bg-white rounded-2xl p-6 shadow-inner border border-emerald-100">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2 uppercase tracking-wide font-medium">
                {t('doorCode.yourCode', 'El vostre codi')}
              </p>
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 shadow-lg">
                <span className="font-mono text-5xl md:text-6xl font-bold text-white tracking-[0.3em]">
                  {doorCode}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {t('doorCode.codeHint', 'Introduïu aquest codi al teclat de la porta')}
              </p>
            </div>
          </div>

          {/* Room Assignment */}
          {assignedRoom && (
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <IconHome className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t('doorCode.assignedRoom', 'Habitació assignada')}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {assignedRoom}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Check-in/out times */}
          {(checkInDate || checkOutDate) && (
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="bg-amber-100 p-3 rounded-xl">
                  <IconClock className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-1">
                    {t('doorCode.stayPeriod', 'Període d\'estada')}
                  </p>
                  <div className="flex items-center gap-2 text-gray-900">
                    {checkInDate && (
                      <span className="font-semibold">{checkInDate}</span>
                    )}
                    {checkInDate && checkOutDate && (
                      <span className="text-gray-400">→</span>
                    )}
                    {checkOutDate && (
                      <span className="font-semibold">{checkOutDate}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Access Instructions */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-lg">📋</span>
              {t('doorCode.instructions', 'Instruccions d\'accés')}
            </h4>
            <div className="text-gray-600 text-sm whitespace-pre-line leading-relaxed">
              {instructions}
            </div>
          </div>

          {/* Location */}
          {locationInfo && (
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="bg-red-100 p-3 rounded-xl">
                  <IconMapPin className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">
                    {propertyName}
                  </h4>
                  {locationInfo.address && (
                    <p className="text-gray-600 text-sm">
                      {locationInfo.address}
                      {locationInfo.city && `, ${locationInfo.city}`}
                    </p>
                  )}
                  {locationInfo.googleMapsUrl && (
                    <a
                      href={locationInfo.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <IconMapPin className="h-4 w-4" />
                      {t('doorCode.openMaps', 'Obrir a Google Maps')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Important notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-800">
              <span className="font-semibold">⚠️ {t('doorCode.important', 'Important')}:</span>{' '}
              {t('doorCode.securityNotice', 'No compartiu aquest codi amb ningú. El codi deixarà de funcionar després del check-out.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
